import React, { useEffect, useRef, useState } from 'react';
import { MiniFighterCard } from './FighterCard';

/**
 * Chat component for rivalry messages
 */
export function RivalryChat({ messages, onSendMessage, currentUserId, className = '' }) {
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!newMessage.trim() || sending) return;

    setSending(true);
    await onSendMessage(newMessage.trim());
    setNewMessage('');
    setSending(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  return (
    <div className={`bg-gray-800 rounded-lg flex flex-col ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-700">
        <h2 className="text-xl font-bold text-white">Chat</h2>
        <p className="text-gray-400 text-sm">{messages.length} messages</p>
      </div>

      {/* Messages Container */}
      <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[400px] max-h-[600px]">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-400 text-center">No messages yet. Be the first to say something!</p>
          </div>
        ) : (
          messages.map((message) => (
            <ChatMessage key={message.id} message={message} isCurrentUser={message.user_id === currentUserId} />
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="p-4 border-t border-gray-700">
        <form onSubmit={handleSendMessage} className="flex gap-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            className="flex-1 bg-gray-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={sending}
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded-lg transition-colors disabled:bg-gray-600 disabled:cursor-not-allowed"
          >
            {sending ? 'Sending...' : 'Send'}
          </button>
        </form>
      </div>
    </div>
  );
}

/**
 * Individual chat message
 */
function ChatMessage({ message, isCurrentUser }) {
  const { message_type, content, created_at, fighter_profiles, metadata } = message;

  // Format timestamp
  const timestamp = new Date(created_at);
  const timeString = timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  // System messages
  if (message_type === 'system') {
    return <SystemMessage content={content} timestamp={timeString} metadata={metadata} />;
  }

  // Achievement messages
  if (message_type === 'achievement') {
    return <AchievementMessage content={content} timestamp={timeString} metadata={metadata} />;
  }

  // User messages
  return (
    <div className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[70%] ${isCurrentUser ? 'order-2' : 'order-1'}`}>
        {/* User info */}
        {!isCurrentUser && fighter_profiles && (
          <div className="mb-1">
            <MiniFighterCard profile={fighter_profiles} />
          </div>
        )}

        {/* Message bubble */}
        <div
          className={`rounded-lg px-4 py-2 ${
            isCurrentUser ? 'bg-blue-600 text-white' : 'bg-gray-700 text-white'
          }`}
        >
          <p className="break-words">{content}</p>
          <p className={`text-xs mt-1 ${isCurrentUser ? 'text-blue-200' : 'text-gray-400'}`}>{timeString}</p>
        </div>
      </div>
    </div>
  );
}

/**
 * System message (join/leave notifications)
 */
function SystemMessage({ content, timestamp, metadata }) {
  return (
    <div className="flex justify-center">
      <div className="bg-gray-700 bg-opacity-50 rounded-lg px-4 py-2 max-w-[80%]">
        <div className="flex items-center justify-center text-gray-300 text-sm">
          <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <span>{content}</span>
        </div>
        <p className="text-xs text-gray-500 text-center mt-1">{timestamp}</p>
      </div>
    </div>
  );
}

/**
 * Achievement message (badges, streaks, etc.)
 */
function AchievementMessage({ content, timestamp, metadata }) {
  return (
    <div className="flex justify-center">
      <div className="bg-gradient-to-r from-yellow-600 to-orange-600 rounded-lg px-4 py-3 max-w-[80%]">
        <div className="flex items-center justify-center text-white font-semibold">
          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
          <span>{content}</span>
        </div>
        <p className="text-xs text-yellow-100 text-center mt-1">{timestamp}</p>
      </div>
    </div>
  );
}

/**
 * Compact chat view (for sidebar or smaller spaces)
 */
export function CompactChat({ messages, onSendMessage, currentUserId, maxMessages = 10, className = '' }) {
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);

  const recentMessages = messages.slice(-maxMessages);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    await onSendMessage(newMessage.trim());
    setNewMessage('');
    setSending(false);
  };

  return (
    <div className={`bg-gray-800 rounded-lg ${className}`}>
      {/* Messages */}
      <div className="p-3 space-y-2 max-h-[300px] overflow-y-auto">
        {recentMessages.length === 0 ? (
          <p className="text-gray-400 text-sm text-center">No messages</p>
        ) : (
          recentMessages.map((message) => (
            <div
              key={message.id}
              className={`text-sm ${
                message.message_type === 'system' ? 'text-gray-400 italic text-center' : 'text-white'
              }`}
            >
              {message.message_type === 'user' && (
                <span className={`font-semibold ${message.user_id === currentUserId ? 'text-blue-400' : 'text-gray-300'}`}>
                  {message.fighter_profiles?.fighter_name || 'Fighter'}:{' '}
                </span>
              )}
              {message.content}
            </div>
          ))
        )}
      </div>

      {/* Input */}
      <div className="p-3 border-t border-gray-700">
        <form onSubmit={handleSendMessage} className="flex gap-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Message..."
            className="flex-1 bg-gray-700 text-white text-sm rounded px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={sending}
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-1 rounded transition-colors disabled:bg-gray-600"
          >
            Send
          </button>
        </form>
      </div>
    </div>
  );
}
